import React, { useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * Click-to-zoom image with pinch/wheel zoom + drag-to-pan in a fullscreen
 * lightbox. The thumbnail uses `object-contain` so the scanned plant is shown
 * in full (no awkward crop). Tap the small expand icon (or anywhere on the
 * image) to open the lightbox.
 *
 * Props:
 *  - src:          image URL or data URL
 *  - alt:          accessible label
 *  - thumbHeight:  px height of the thumbnail (default 224)
 *  - className:    optional extra classes for the wrapping div
 *  - testid:       data-testid for tests (default "zoomable-image")
 */
export default function ZoomableImage({ src, alt = "", thumbHeight = 224, className = "", testid = "zoomable-image" }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef(null);
  const lastPinchRef = useRef(null);

  // Reset transform whenever the lightbox is closed/opened
  useEffect(() => {
    if (!open) {
      setScale(1); setTx(0); setTy(0);
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(5, s + 0.25));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.25));
      if (e.key === "0") { setScale(1); setTx(0); setTy(0); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.0025;
    setScale((s) => Math.min(5, Math.max(1, s + delta)));
  };

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { x: e.clientX - tx, y: e.clientY - ty };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setTx(e.clientX - dragRef.current.x);
    setTy(e.clientY - dragRef.current.y);
  };
  const onMouseUp = () => { dragRef.current = null; };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchRef.current = Math.hypot(dx, dy);
    } else if (e.touches.length === 1 && scale > 1) {
      dragRef.current = { x: e.touches[0].clientX - tx, y: e.touches[0].clientY - ty };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastPinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / lastPinchRef.current;
      setScale((s) => Math.min(5, Math.max(1, s * ratio)));
      lastPinchRef.current = dist;
    } else if (e.touches.length === 1 && dragRef.current) {
      setTx(e.touches[0].clientX - dragRef.current.x);
      setTy(e.touches[0].clientY - dragRef.current.y);
    }
  };
  const onTouchEnd = () => { lastPinchRef.current = null; dragRef.current = null; };

  if (!src) return null;

  return (
    <>
      {/* Thumbnail wrapper — checkerboard-style dark backdrop so portrait/landscape both look intentional */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`relative w-full block group ${className}`}
        style={{
          height: thumbHeight,
          background:
            "linear-gradient(135deg, rgba(8,14,10,0.72), rgba(20,32,24,0.72))",
          overflow: "hidden",
        }}
        data-testid={testid}
        aria-label="View image in full size"
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-contain transition-transform group-hover:scale-[1.02]"
          style={{ filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.55))" }}
        />
        <span
          className="absolute top-2 right-2 inline-flex items-center justify-center w-8 h-8 rounded-full transition-opacity opacity-80 group-hover:opacity-100"
          style={{ background: "rgba(8,14,10,0.78)", border: "1px solid rgba(255,255,255,0.18)", backdropFilter: "blur(6px)" }}
          data-testid={`${testid}-expand`}
        >
          <Maximize2 size={14} className="text-white" />
        </span>
      </button>

      {/* Fullscreen lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.96)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          data-testid={`${testid}-lightbox`}
        >
          {/* Close */}
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors z-10"
            style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.18)" }}
            data-testid={`${testid}-close`}
            aria-label="Close"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Zoom controls */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-full p-1.5"
            style={{ background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}
          >
            <button
              onClick={() => setScale((s) => Math.max(1, s - 0.25))}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10"
              data-testid={`${testid}-zoom-out`}
              aria-label="Zoom out"
            >
              <ZoomOut size={16} className="text-white" />
            </button>
            <span className="text-xs text-white font-semibold w-12 text-center select-none" data-testid={`${testid}-zoom-level`}>
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={() => setScale((s) => Math.min(5, s + 0.25))}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10"
              data-testid={`${testid}-zoom-in`}
              aria-label="Zoom in"
            >
              <ZoomIn size={16} className="text-white" />
            </button>
            {scale !== 1 && (
              <button
                onClick={() => { setScale(1); setTx(0); setTy(0); }}
                className="px-3 h-10 rounded-full text-xs text-white hover:bg-white/10 font-semibold"
                data-testid={`${testid}-zoom-reset`}
              >
                Reset
              </button>
            )}
          </div>

          {/* Image canvas */}
          <div
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className="w-full h-full flex items-center justify-center select-none"
            style={{ cursor: scale > 1 ? "grab" : "zoom-in" }}
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="max-w-[92vw] max-h-[88vh] object-contain transition-transform"
              style={{
                transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                transformOrigin: "center center",
              }}
              data-testid={`${testid}-lightbox-img`}
              onDoubleClick={() => {
                if (scale > 1) { setScale(1); setTx(0); setTy(0); }
                else setScale(2);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
